/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import {
  Card,
  CardContent,
  Control,
  ControlHelper,
  Input,
  Label,
  Button,
  PageLoader,
  ControlError,
  Toggle,
  Select,
  SelectOption,
  Modal,
  Header,
  Logo,
  ToastContext,
  Checkbox,
} from "@forge/common";
import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  BASIC_INFO_MAX_CHARS,
  BASIC_INFO_MIN_CHARS,
  IAM_PROJECT_ID_SESSION_TOKEN_STORAGE_NAME,
  LOCALE_CONST,
  RBM_ACCESS_TOKEN_STORAGE_NAME,
} from "../../utils/constants";
import "./styles.scss";
import {
  useCreateDeviceMutation,
  useGetHardwareDetailsQuery,
} from "../../services/api";
import { setDeviceListUpdated, setHeaderDetails } from "../../actions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigateToLandingPage } from "../../utils/navigationUtils";
import { ConfigurationVariables } from "../../config";
import { useFlags } from "launchdarkly-react-client-sdk";
import { validateSpecialChar, validateAlphanumeric, validateHardwareModel } from "../../utils/utility";
import { ILanguageContextualization } from "../../models/createdevice";
import LogoutButton from "./LogoutButton";
import { useTranslation } from "react-i18next";
import { OrgListProps, LinkGatewayResult } from "../../models";
import { RootState } from "../../store";
import HardwareDetails from "../EBICognition/HardwareDetails";
import { NetworkErrObj } from "../gateway-report/utils/gatewayTypes";
import { getErrorInfo } from "../../reusablecomponents/errorInfo/getErrorInfo";

const CreateDevice = (props: {
  type?: string;
  loadType?: string;
  setClearToast?: (clearToastFn: () => void) => void;
  // Only the Assign gateway panel passes this: the button then registers and links to the site.
  siteLink?: {
    link: (deviceId: string) => Promise<LinkGatewayResult>;
    onDone: () => void;
    onCancel: () => void;
  };
}) => {
  const REGISTRATION_KEY_LENGTH = 6;
  const TIMEOUT_DURATION = 3000;
  const { addSuccess, addError, removeAll } = useContext(ToastContext);

  const flags = useFlags();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // Device type options

  const { selectedOrg, siOrgLists } = useSelector(
    (state: RootState) => state.global,
  );
  const convertFn = (value: string) => t(value);
  const [reqObject, setReqObject] = useState<any>({
    name: "",
    registrationKey: "",
    migration: false,
    systemGUID: "",
    isDisconnected: false,
    // language: flags.gatewayLanguageContextualization?.[0]?.value || ''
  });
  const [hardwareData, setHardwareData] = useState({
    deviceType: "",
    hardwareModel: "",
    hardwareID: "",
    encryptionKey: "",
  });
  const [createDevice, { isLoading }] = useCreateDeviceMutation(); // Mutation hook
  // Covers the link step after register, when the create mutation is already finished.
  const [isLinking, setIsLinking] = useState(false);
  const { data: hardwareDetailsData, error: hardwareDetailsError, isLoading: isHardwareDetailsTypeLoading } =
    useGetHardwareDetailsQuery(undefined, {
      skip: !reqObject.isDisconnected,
    });
  // First, get availableDeviceTypesFromConfig as an array
  const availableDeviceTypesFromConfig = ConfigurationVariables.ALLOWED_DISCONNECTED_GATEWAY_TYPES?.split(" ") || [];

  // Device type options - intersection of config allowed types and API data
  const availableDeviceTypes = useMemo(() => {
    if (isHardwareDetailsTypeLoading || !hardwareDetailsData) return [];

    // Filter hardwareDetailsData to only include types that are in availableDeviceTypesFromConfig
    return hardwareDetailsData.filter((item: any) =>
      availableDeviceTypesFromConfig.includes(item.deviceType || item)
    );
  }, [hardwareDetailsData, isHardwareDetailsTypeLoading, availableDeviceTypesFromConfig]);

  // Set default device type for disconnected devices
  useEffect(() => {
    if (reqObject.isDisconnected && !isHardwareDetailsTypeLoading && hardwareDetailsData?.length > 0 && !hardwareData.deviceType) {
      const defaultDeviceType = availableDeviceTypes.length > 0
        ? (availableDeviceTypes[0])
        : "";
      setHardwareData(prev => ({ ...prev, deviceType: defaultDeviceType }));
    }
  }, [reqObject.isDisconnected, isHardwareDetailsTypeLoading, hardwareDetailsData, availableDeviceTypes, hardwareData.deviceType]);

  const [showToast, setShowToast] = useState(false);
  const [showStandaloneSuccessModal, setShowStandaloneSuccessModal] =
    useState(false);
  const [toastType, setToastType] = useState<
    "error" | "success" | "warning" | "progress"
  >("success");
  const [toastContent, setToastContent] = useState("");
  const [title, setTitle] = useState(t(LOCALE_CONST.untitled));
  const { navigateToLandingPage } = useNavigateToLandingPage();
  const [errorState, setErrorState] = useState({
    showNameError: false,
    showRegistrationKeyError: false,
    showValidRegistrationKeyError: false,
    showSystemGUIDError: false,
    showValidSystemGUIDError: false,
    showNameTypeError: false,
  });
  const [hardwareErrorState, setHardwareErrorState] = useState({
    showDeviceTypeError: false,
    showHardwareModelError: false,
    showHardwareIDError: false,
    showValidHardwareIDError: false,
    showHardwareModelTypeError: false,
    showEncryptionKeyError: false,
  });

  const [showSystemGUIDFiled, setShowSystemGUIDFiled] = useState(false);

  // To update the error state
  const setError = (field: string, value: boolean) => {
    setErrorState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  // Validation helpers are imported from utils/utility for consistency

  // To update the hardware error state
  const setHardwareError = (field: string, value: boolean) => {
    setHardwareErrorState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    if (type === "name") {
      setError("showNameError", false);
      setError("showNameTypeError", false);
      setReqObject({ ...reqObject, name: e.target.value });
      if (/ {2}|%/.test(e.target.value)) {
        setError("showNameTypeError", true);
      }
    } else if (type === "registrationKey") {
      setError("showRegistrationKeyError", false);
      setError("showValidRegistrationKeyError", false);
      setReqObject({ ...reqObject, registrationKey: e.target.value });
      const isValidRegKey =
        e.target.value.length !== REGISTRATION_KEY_LENGTH ||
        e.target.value !== e.target.value.toUpperCase();
      if (
        (isValidRegKey || !validateSpecialChar(e.target.value)) &&
        e.target.value !== ""
      )
        setError("showValidRegistrationKeyError", true);
    } else if (type === "migration") {
      setError("showSystemGUIDError", false);
      if (e.target.checked) {
        setReqObject({ ...reqObject, migration: e.target.checked });
      } else {
        setReqObject({
          ...reqObject,
          migration: e.target.checked,
          systemGUID: "",
        });
      }
    } else if (type === "systemGUID") {
      setError("showSystemGUIDError", false);
      setError("showValidSystemGUIDError", false);
      setReqObject({ ...reqObject, systemGUID: e.target.value });
      if (!validateSpecialChar(e.target.value))
        setError("showValidSystemGUIDError", true);
    }
  };

  // Handle hardware input changes
  const handleHardwareInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    type: string,
  ) => {
    if (type === "deviceType") {
      setHardwareError("showDeviceTypeError", false);
      // Reset hardwareModel when deviceType changes
      setHardwareData({
        ...hardwareData,
        deviceType: e.target.value,
        hardwareModel: "",
      });
    } else if (type === "hardwareModel") {
      setHardwareError("showHardwareModelError", false);
      setHardwareError("showHardwareModelTypeError", false);
      setHardwareData({ ...hardwareData, hardwareModel: e.target.value });
      if (e.target.value !== "" && !validateHardwareModel(e.target.value)) {
        setHardwareError("showHardwareModelTypeError", true);
      }
    } else if (type === "hardwareID") {
      setHardwareError("showHardwareIDError", false);
      setHardwareError("showValidHardwareIDError", false);
      setHardwareData({ ...hardwareData, hardwareID: e.target.value });
      if (e.target.value !== "" && !validateAlphanumeric(e.target.value)) {
        setHardwareError("showValidHardwareIDError", true);
      }
    } else if (type === "encryptionKey") {
      setHardwareError("showEncryptionKeyError", false);
      setHardwareData({ ...hardwareData, encryptionKey: e.target.value });
    }
  };

  const handleCancel = () => {
    if (props.siteLink) {
      props.siteLink.onCancel();
      return;
    }

    navigateToLandingPage();
  };

  // Register succeeded: link the new gateway (response `deviceId`) to the selected site.
  const linkRegisteredGateway = async (result: { deviceId?: unknown } | null | undefined) => {
    const newDeviceId = typeof result?.deviceId === "string" ? result.deviceId : "";
    const outcome: LinkGatewayResult = newDeviceId
      ? await props.siteLink.link(newDeviceId)
      : "failed";
    const toast = {
      id: "uop-toast-message",
      className: "uop-toast-message",
      title: "",
      subtitle: "",
      showClose: true,
      showDismiss: false,
      lifetime: TIMEOUT_DURATION + 2000,
    };

    if (outcome === "linked") {
      addSuccess({
        ...toast,
        content: t(LOCALE_CONST.gatewayRegisteredAndLinked, {
          gatewayName: reqObject.name.trim(),
        }),
      });
    } else if (outcome === "pending") {
      addError({ ...toast, content: t(LOCALE_CONST.gatewayPendingCannotLink) });
    } else {
      addError({ ...toast, content: t(LOCALE_CONST.gatewayCreatedLinkFailed) });
    }

    props.siteLink.onDone();
  };

  const handleCreate = async () => {
    if (isLinking) {
      return;
    }

    // Validate basic info fields
    //validation for disconnected device creation
    if (reqObject.isDisconnected) {
      const hasNameError = reqObject.name === "";
      const hasDeviceTypeError = hardwareData.deviceType.trim() === "";
      const hasModelError = hardwareData.hardwareModel === "";
      const hasIDError = hardwareData.hardwareID === "";
      const hasEncryptionKeyError = hardwareData.encryptionKey.trim() === "";
      const hasInvalidID =
        hardwareData.hardwareID !== "" &&
        !validateAlphanumeric(hardwareData.hardwareID);
      const hasInvalidModel =
        hardwareData.hardwareModel !== "" &&
        !validateHardwareModel(hardwareData.hardwareModel);

      if (
        hasNameError ||
        hasDeviceTypeError ||
        hasModelError ||
        hasIDError ||
        hasEncryptionKeyError ||
        hasInvalidID ||
        hasInvalidModel
      ) {
        setError("showNameError", hasNameError);
        setHardwareError("showDeviceTypeError", hasDeviceTypeError);
        setHardwareError("showHardwareModelError", hasModelError);
        setHardwareError("showHardwareIDError", hasIDError);
        setHardwareError("showEncryptionKeyError", hasEncryptionKeyError);
        setHardwareError("showValidHardwareIDError", hasInvalidID);
        setHardwareError("showHardwareModelTypeError", hasInvalidModel);
        return;
      }
    }

    //validation for connected device creation
    if (!reqObject.isDisconnected) {
      if (
        reqObject.name === "" &&
        reqObject.registrationKey === "" &&
        showSystemGUIDFiled &&
        reqObject.systemGUID === ""
      ) {
        setError("showNameError", true);
        setError("showRegistrationKeyError", true);
        setError("showSystemGUIDError", true);
        return;
      } else if (reqObject.name === "" && reqObject.registrationKey === "") {
        setError("showNameError", true);
        setError("showRegistrationKeyError", true);
        return;
      } else if (reqObject.name === "") {
        setError("showNameError", true);
        return;
      } else if (reqObject.registrationKey === "") {
        setError("showRegistrationKeyError", true);
        return;
      } else if (
        reqObject.registrationKey.length !== REGISTRATION_KEY_LENGTH ||
        reqObject.registrationKey !== reqObject.registrationKey.toUpperCase()
      ) {
        setError("showValidRegistrationKeyError", true);
        return;
      } else if (!validateSpecialChar(reqObject.registrationKey)) {
        setError("showValidRegistrationKeyError", true);
        return;
      } else if (showSystemGUIDFiled && reqObject.systemGUID === "") {
        setError("showSystemGUIDError", true);
        return;
      } else if (
        showSystemGUIDFiled &&
        !validateSpecialChar(reqObject.systemGUID)
      ) {
        setError("showValidSystemGUIDError", true);
        return;
      } else if (
        reqObject.name.length < BASIC_INFO_MIN_CHARS ||
        reqObject.name.length > BASIC_INFO_MAX_CHARS
      ) {
        showErrorToast(t(LOCALE_CONST.gwNameValidation));
        return;
      }
    }

    // If all basic info is valid, proceed with device creation
    // const languageAppend = `(${t(LOCALE_CONST.language)}:${reqObject.language})`;
    try {
      const payload = reqObject.isDisconnected
        ? {
          name: reqObject.name,
          isDisconnected: true,
          gatewayType: hardwareData.deviceType,
          model: hardwareData.hardwareModel,
          hardwareId: hardwareData.hardwareID,
          encryptionKey: hardwareData.encryptionKey.trim(),
        }
        : {
          name: reqObject.name,
          registrationKey: reqObject.registrationKey,
          migration: reqObject.migration,
          isDisconnected: false,
          ...(reqObject.migration && { systemGuid: reqObject.systemGUID }),
        };
      const result = await createDevice(payload).unwrap();
      if (props.siteLink) {
        setIsLinking(true);
        try {
          await linkRegisteredGateway(result);
        } finally {
          setIsLinking(false);
        }
        return;
      }
      if (result) {
        if (props?.type === "standalone") {
          setShowStandaloneSuccessModal(true);
        } else {
          showSuccessToast(
            t(LOCALE_CONST.gwNameSuccessCreate, {
              gatewayName: reqObject.name.trim(),
            }),
          );
        }
        dispatch(setDeviceListUpdated({ deviceListUpdated: true }));
        setReqObject({
          name: "",
          registrationKey: "",
          migration: false,
          systemGUID: "",
        });
      }
    } catch (error) {
      handleApiError(error); // Handle specific error cases
    }
  };

  useEffect(() => {
    dispatch(
      setHeaderDetails({
        headerDetails: {
          status: "",
          type: "create",
          title: t(LOCALE_CONST.createNewGateway),
        },
      }),
    );
  }, []);

  useEffect(() => {
    // Standalone login only: in the Assign panel the parent supplies the token and this would overwrite it.
    if (!props.siteLink && sessionStorage.getItem("oidc.default")) {
      const tokenVal =
        JSON.parse(sessionStorage.getItem("oidc.default") || "{}")?.tokens
          ?.accessToken || "";
      sessionStorage.setItem(RBM_ACCESS_TOKEN_STORAGE_NAME, tokenVal);
      dispatch(setDeviceListUpdated({ deviceListUpdated: true }));
    }
  }, []);

  // Helper functions
  const showSuccessToast = (message: any) => {
    setToastType("success");
    setToastContent(message);
    setShowToast(true);
  };

  const showErrorToast = (message: string) => {
    setToastType("error");
    setToastContent(message);
    setShowToast(true);
  };

  /**
   * To set the error for toast
   * @param statusCode number
   */
  const displayErrorToast = (
    statusCode: number,
    errMsg?: string,
    fetchConfig?: boolean,
  ) => {
    const errObj = getErrorInfo(statusCode, convertFn);
    if (!fetchConfig) {
      showErrorToast(errMsg || errObj.description);
    } else {
      showErrorToast(t(LOCALE_CONST.noRespReceivedSystem));
    }
  };

  const getErrorObjValue = (error: NetworkErrObj) => {
    if (!error) {
      return "";
    } else if (typeof error.data === "object") {
      return error.data?.Message || "";
    } else if (typeof error.data === "string") {
      return error.data || "";
    } else {
      return "";
    }
  };

  const handleApiError = (error: any) => {
    const errorMessages: { [key: number]: string } = {
      400: t(LOCALE_CONST.notSaveGWDetails),
      401: t(LOCALE_CONST.unAuthFails),
      403: t(LOCALE_CONST.AuthForbidFails),
      404: t(LOCALE_CONST.clientError_404),
      409: t(LOCALE_CONST.gwNameAlreadyExists, { gatewayName: reqObject.name }),
      500: t(LOCALE_CONST.internalServerError),
      502: t(LOCALE_CONST.badGatewayTryAgainLater),
      503: t(LOCALE_CONST.serviceUnavailTryAgainLater),
      504: t(LOCALE_CONST.gwTimeoutRequestTryAgainLater),
    };

    if (error?.data?.message?.match?.(/Registration/)) {
      errorMessages[404] = t(LOCALE_CONST["404InvalidRegKey"], {
        regKey: reqObject.registrationKey,
      });
      errorMessages[410] = t(LOCALE_CONST.regKeyExpired);
    }

    showErrorToast(
      errorMessages[error?.status] || t(LOCALE_CONST.somethingWentWrong),
    );
  };

  const clearToast = () => {
    setShowToast(false);
    removeAll(); // Clear all toast notifications
  };

  // Expose the clearToast function to the parent component
  useEffect(() => {
    if (props.setClearToast) {
      props.setClearToast(clearToast);
    }
  }, [props.setClearToast]);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        if (props.type !== "standalone") {
          if (toastType === "success") {
            navigateToLandingPage();
            removeAll();
          } else if (toastType === "error") {
            removeAll();
          }
        }
      }, TIMEOUT_DURATION);

      return () => clearTimeout(timer);
    }
  }, [showToast, toastType, props.type]);

  // useEffect(() => {
  //   if (showToast) {
  //     const timer = setTimeout(() => {
  //       setShowToast(false);
  //       if (props.type !== "standalone") {
  //         if (toastType === "success") {
  //           navigateToLandingPage();
  //           removeAll();
  //         } else if (toastType === "error") {
  //           removeAll();
  //         }
  //       }
  //     }, TIMEOUT_DURATION);

  //     return () => clearTimeout(timer);
  //   }
  // }, [showToast, toastType, props.type]);

  useEffect(() => {
    if (showToast) {
      if (toastType === "success") {
        addSuccess({
          id: "uop-toast-message",
          className: "uop-toast-message",
          title: "",
          subtitle: "",
          showClose: true,
          showDismiss: false,
          content: toastContent,
        });
      } else if (toastType === "error") {
        console.log("error jb", toastContent);
        addError({
          id: "uop-toast-message",
          className: "uop-toast-message",
          title: "",
          subtitle: "",
          showClose: true,
          showDismiss: false,
          content: toastContent,
        });
      }
    }
  }, [showToast, toastType, toastContent]);

  useEffect(() => {
    if (reqObject.name === "") {
      setTitle(t(LOCALE_CONST.untitled));
    } else {
      setTitle(reqObject.name);
    }
  }, [reqObject.name]);

  const handelMigrationToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowSystemGUIDFiled(!showSystemGUIDFiled);
    handleInputChange(e, "migration");
  };

  // const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   if (event.target.value !== "") {
  //     setReqObject({ ...reqObject, language: event.target.value });
  //     return;
  //   }

  // };

  const renderWarnWindow = () => {
    if (props?.loadType === "noSIOrg") {
      return (
        <Modal title={t(LOCALE_CONST.warning)} className="confirmation-modal">
          <div className="confirmation-modal-content">
            {t(LOCALE_CONST.noOrgAssociated)}
          </div>
        </Modal>
      );
    } else if (props?.loadType === "error") {
      return (
        <Modal title={t(LOCALE_CONST.error)} className="confirmation-modal">
          <div className="confirmation-modal-content">
            {t(LOCALE_CONST.orgListFailed)}
          </div>
          <div className="confirmation-modal-footer">
            <Button variant="primary" onClick={() => window.location.reload()}>
              {t(LOCALE_CONST.refresh)}
            </Button>
          </div>
        </Modal>
      );
    } else {
      return "";
    }
  };

  const onOrgChange = (value: string) => {
    dispatch({
      type: "SET_ORG_LIST",
      payload: {
        selectedOrg: value,
      },
    });
    sessionStorage.setItem(IAM_PROJECT_ID_SESSION_TOKEN_STORAGE_NAME, value);
  };

  const headerRightSlot = (
    <div className="header-right-slot">
      <Select
        aria-label="Shape"
        defaultValue={selectedOrg}
        height={5}
        id="shape-select"
        onChange={(e: any) => onOrgChange(e.target.value)}
        placeholder={t(LOCALE_CONST.selectOrg)}
        size="large"
      >
        {siOrgLists.map((item: OrgListProps) => (
          <SelectOption key={item.value} label={item.text} value={item.value} />
        ))}
      </Select>
      <LogoutButton />
    </div>
  );
  const enableEbiCognition = flags.enableEbiCognition;
  return (
    <>
      {renderWarnWindow()}
      <PageLoader
        isLoading={props?.loadType === "loading" || isLoading || isLinking || false}
      />
      {props?.type === "standalone" && (
        <Header
          logo={<Logo domain={t(LOCALE_CONST.gatewayManagement)} />}
          onMenuClick={function noRefCheck() { }}
          rightSlot={headerRightSlot}
          showMenuToggle={false}
          className="stand-alone-header"
        />
      )}
      <div>
        <Card className="mx-auto mt-6 w-[25rem] min-w-full max-w-2xl edit-dev-card-block-cls">
          <span className="iot-basic-info-text p-5 block">
            {t(LOCALE_CONST.basicInfo)}
          </span>
          <CardContent top="none">
            <Control className="mb-4 w-[50%] mt-2" spacingAbove spacingBelow>
              <Label className="font-normal" required requiredText="">
                {t(LOCALE_CONST.name)}
              </Label>
              <Input
                id="name"
                value={reqObject.name}
                onChange={(e) => handleInputChange(e, "name")}
                className={
                  errorState.showNameError || errorState.showNameTypeError
                    ? "error-input-text"
                    : "normal-input"
                }
                error={errorState.showNameError || errorState.showNameTypeError}
                required
                data-testid="test-gateway-name"
                maxLength={50}
              />
              {errorState.showNameTypeError && (
                <ControlError showIcon>
                  {t(LOCALE_CONST.invalidCharacter)}
                </ControlError>
              )}
              {errorState.showNameError && (
                <ControlError showIcon>
                  {t(LOCALE_CONST.requiredField)}
                </ControlError>
              )}
              <ControlHelper className="helper-text">
                {t(LOCALE_CONST.gatewayNameValidText)}
              </ControlHelper>
            </Control>
            {/* Added checkbox for disconnected devices */}
            {enableEbiCognition && (
              <Checkbox
                id="isDisconnected"
                className="mt-2"
                checked={reqObject.isDisconnected}
                data-testid="test-disconnected-checkbox"
                onChange={(e) => {
                  setReqObject({
                    ...reqObject,
                    isDisconnected: e.target.checked,
                  });
                }}
              >
                {t(LOCALE_CONST.disconnectedDeviceCheckedMsg)}
              </Checkbox>
            )}

            {!reqObject.isDisconnected && (
              <div className="connected-gateway-section">
                <Control className="mb-4 w-[50%] mt-2">
                  <Label className="font-normal" required requiredText="">
                    {t(LOCALE_CONST.enterRegKey)}
                  </Label>
                  <Input
                    id="registrationKey"
                    onChange={(e) => handleInputChange(e, "registrationKey")}
                    className={
                      errorState.showRegistrationKeyError
                        ? "error-input-text"
                        : "normal-input"
                    }
                    error={errorState.showRegistrationKeyError}
                    data-testid="test-registration-key"
                    value={reqObject.registrationKey}
                    maxLength={6}
                  />
                  {errorState.showRegistrationKeyError && (
                    <ControlError showIcon>
                      {t(LOCALE_CONST.requiredField)}
                    </ControlError>
                  )}
                  {errorState.showValidRegistrationKeyError && (
                    <ControlError showIcon>
                      {t(LOCALE_CONST.regKeyValidation)}
                    </ControlError>
                  )}
                  <ControlHelper className="helper-text">
                    {t(LOCALE_CONST.manualKeyGenerText)},
                    <a
                      className="text-blue-500"
                      href={`mailto:${ConfigurationVariables.HONEYWELL_SUPPORT_EMAILID}`}
                    >
                      {" "}
                      {t(LOCALE_CONST.contact)}{" "}
                    </a>
                    {t(LOCALE_CONST.systemIntegrator)}
                  </ControlHelper>
                </Control>
                <div
                  className={`w-[34rem] mt-2 p-0 ${showSystemGUIDFiled ? "mb-4" : ""
                    }`}
                >
                  <Label
                    className="font-normal"
                    optional
                    optionalText={`- ${t(LOCALE_CONST.optional)}`}
                  >
                    {t(LOCALE_CONST.existingGateway)}
                  </Label>
                  <p className="helper-text-readable">
                    {t(LOCALE_CONST.upgradeCurrGateway)}
                  </p>
                  <Toggle
                    id="migration"
                    onChange={handelMigrationToggle}
                    data-testid="test-migration"
                  />
                </div>
                {showSystemGUIDFiled && (
                  <div className="w-[50%] mt-2 pb-0">
                    <Label
                      className="font-normal pb-2"
                      required
                      requiredText=""
                    >
                      {t(LOCALE_CONST.enterSystemGUID)}
                    </Label>
                    <Input
                      id="systemGUID"
                      className={
                        errorState.showSystemGUIDError
                          ? "error-input-text"
                          : "normal-input"
                      }
                      onChange={(e) => handleInputChange(e, "systemGUID")}
                      error={errorState.showSystemGUIDError}
                      data-testid="test-system-guid"
                      value={reqObject.systemGUID}
                      maxLength={50}
                    />
                    {errorState.showSystemGUIDError && (
                      <ControlError showIcon>
                        {t(LOCALE_CONST.requiredField)}
                      </ControlError>
                    )}
                    {errorState.showValidSystemGUIDError && (
                      <ControlError showIcon>
                        {t(LOCALE_CONST.enterValidSystemGUID)}
                      </ControlError>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* <Control className="w-[34rem] mt-2 pb-0" >
              <Label>{t(LOCALE_CONST.langNameConvention)}</Label>
              <Select aria-label="Language" value={reqObject.language} multiple={false} id="language-select" onChange={handleLanguageChange} data-testid="test-language">
                {flags.gatewayLanguageContextualization?.map((language: ILanguageContextualization) => (
                  <SelectOption key={language?.value} value={language?.value} label={language?.label} />
                ))}
              </Select>
            </Control> */}
          </CardContent>
        </Card>
        {reqObject.isDisconnected && (
          <HardwareDetails
            hardwareData={hardwareData}
            errorState={hardwareErrorState}
            deviceTypeOptions={availableDeviceTypes}
            onInputChange={handleHardwareInputChange}
            isLoading={isHardwareDetailsTypeLoading}
            showPublicKey={reqObject.isDisconnected}
            keyFieldLabel={LOCALE_CONST.publicKey}
            keyFieldType="encryptionKey"
          />
        )}
      </div>

      <div className="create-device-button-Container flex space-x-4 mt-6">
        {props?.type !== "standalone" && (
          <Button variant="secondary" onClick={handleCancel}>
            {t(LOCALE_CONST.cancel)}
          </Button>
        )}
        <Button variant="primary" onClick={handleCreate}>
          {props.siteLink
            ? t(LOCALE_CONST.registerAndLinkGateway)
            : t(LOCALE_CONST.save)}
        </Button>
      </div>
      {/* Confirmation Modal for Standalone only */}
      {props?.type === "standalone" && showStandaloneSuccessModal && (
        <Modal title="" className="standalone-confirmation-modal">
          <div className="modal-content text-center">
            <div className="flex justify-center">
              <img
                src="/img/standalone-success.svg"
                alt="Standalone Success"
                className="standalone-success-img"
              />
            </div>
            <div className="modal-title">
              <h3 className="pt-4 pb-4">
                {t(LOCALE_CONST.gatewayConfirmationTitle)}
              </h3>
            </div>
            <div className="modal-subtitle">
              <p>{t(LOCALE_CONST.gatewayConfirmationText)}</p>
            </div>

            <div className="modal-footer-container">
              <LogoutButton isModalButton={true} />
              <Button
                variant="primary"
                onClick={() => {
                  setShowStandaloneSuccessModal(false);
                }}
                size="large"
              >
                Register New gateway
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default CreateDevice;

